/**
 * Firebase ID token for API calls — the server derives uid / e-mail /
 * realm from this token only (server/auth_tokens.py). X-User-UID and
 * X-User-Email are no longer sent.
 *
 * Inside a native shell the native app's session wins (see nativeAuth);
 * on the web it is whichever realm holds the active Firebase session.
 * `getIdToken()` returns a cached token and refreshes it on expiry.
 */
import type { User } from "firebase/auth";
import { currentUser } from "./firebaseApp";
import { getNativeIdToken, isNativeAuthShell } from "./nativeAuth";

export async function getIdToken(
  opts: { forceRefresh?: boolean; user?: User | null } = {},
): Promise<string | null> {
  if (!opts.user && isNativeAuthShell()) {
    const t = await getNativeIdToken(!!opts.forceRefresh);
    if (t) return t;
  }
  const u = opts.user ?? currentUser();
  if (!u) return null;
  try {
    return await u.getIdToken(!!opts.forceRefresh);
  } catch {
    return null;
  }
}

/** `{Authorization: "Bearer …"}` for `fetch()` calls (empty when signed out). */
export async function authHeaders(user?: User | null): Promise<Record<string, string>> {
  const t = await getIdToken({ user: user ?? undefined });
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** True when `url` targets our own API, so identity headers may be sent. */
export function isOwnApiUrl(url: string | undefined): boolean {
  if (!url) return false;
  if (url.startsWith("/")) return !url.startsWith("//");
  try {
    const target = new URL(url, window.location.href);
    const base = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
    if (base && url.startsWith(base + "/")) return true;
    return target.origin === window.location.origin || target.origin === "https://electronvisual.org";
  } catch {
    return false;
  }
}
